<script>
import { PhxsgContainer } from '@sg/src/View/Components/Atoms/Container';
import { PhxsgSection } from '@sg/src/View/Components/Atoms/Section';
import { PhxsgButton } from '@sg/src/View/Components/Atoms/Button';
import MixedGridFilters from '@maison/components/mixedGrid/MixedGridFilters.vue';
import MovementGridPanel from '@maison/components/movement/MovementGridPanel.vue';
import { showPanel } from '@phoenix/helpers/panel-helper';
import MixedGrid from '@phoenix/mixins/components/mixedGrid/MixedGrid';
import { forEach, map } from 'lodash-es';
import debounce from 'lodash-es/debounce';
import MovementGridItem from './MovementGridItem';

export default {
  components: {
    PhxsgContainer,
    PhxsgSection,
    PhxsgButton,
    MixedGridFilters,
    MovementGridItem,
    MovementGridPanel,
  },

  mixins: [MixedGrid],

  props: {
    alignedCenter: {
      type: Boolean,
      default: false,
    },
    headingTag: {
      default: 'h2',
      type: String,
    },
    watchesByMovementId: {
      type: Object,
      required: true,
    },
    itemsPerPageTight: {
      type: Number,
      default: 13,
    },
    hasEvenStart: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedMovement: null,
      hasPanelMedias: false,
      searchTerm: '',
    };
  },

  computed: {
    hasHeadings() {
      return this.title || this.subtitle;
    },

    loadMoreClasses() {
      return [
        'actions actions--center mt-md',
        map(this.maxPage, (page, size) => {
          const prefix = size !== 'xs' ? `${size}:` : '';
          return this.page >= page ? `${prefix}hidden` : `${prefix}block`;
        }),
      ];
    },

    gridClasses() {
      return ['movements-grid', { 'movements-grid--even-start': this.hasEvenStart }];
    },

    panelId() {
      return `panel-${this.uniqueId}`;
    },

    watches() {
      if (!this.selectedMovement) {
        return [];
      }
      return this.watchesByMovementId[this.selectedMovement.pageId] ?? [];
    },

    hasFilters() {
      return this.filters && this.filters.length > 0;
    },

    searchFilteredColumns() {
      const trimmedSearchTerm = this.searchTerm.trim();

      if (!trimmedSearchTerm) {
        return this.filteredColumns;
      }

      return this.filteredColumns.filter((column) => {
        const item = column.data;
        const searchableValue = item.searchableValue.toLowerCase().trim();

        return searchableValue.includes(trimmedSearchTerm.toLowerCase());
      });
    },

    subHeadingTag() {
      return this.headingTag === 'h1' ? 'h2' : 'h3';
    },
  },

  methods: {
    onClick(item) {
      this.selectedMovement = item;
      if (window.tracking && item.modelReference) {
        window.tracking.pushEvent(window.tracking.getMovementSelectedTracking(item.modelReference));
      }
      // This is needed as two columns prop on panel must be updated before the panel is shown
      setTimeout(() => showPanel(this.panelId), 200);
    },

    debouncedSearch: debounce(function (value) {
      this.areSortOrFiltersTouched = true;
      this.searchTerm = value.target.value;
    }, 150),

    getColumnDisplayClasses(column) {
      if (!this.shouldDisplayLoadMore) {
        return [];
      }

      return map(column.page, (page, size) => {
        const prefix = size !== 'lg' ? `max-${size}:` : '';
        return this.page < page ? `${prefix}hidden` : '';
      });
    },

    initColumns() {
      this.columns = this.products.map((item, index) => {
        const columnCount = index + 1;
        const columnPagePerSize = {};
        const mdColumnPage = Math.ceil(columnCount / this.itemsPerPage);
        const columnPage = Math.ceil(columnCount / this.itemsPerPageTight);

        forEach(Object.keys(this.widths), (size) => {
          columnPagePerSize[size] = ['md', 'lg'].indexOf(size) > -1 ? mdColumnPage : columnPage;
        });

        if (columnCount === this.products.length) {
          this.maxPage = { ...columnPagePerSize };
        }

        return {
          data: item,
          template: 'product',
          page: columnPagePerSize,
          size: 1,
        };
      });
    },
  },
};
</script>

<template>
  <PhxsgContainer :class="{ 'has-even-start': hasEvenStart }" data-cy="movement-grid">
    <div v-if="hasHeadings" :class="['headings', 'headings--base', { 'text-center': alignedCenter }]">
      <component :is="headingTag" v-if="title" class="h2">{{ title }}</component>
      <component :is="subHeadingTag" v-if="subtitle" class="headings__subtitle h6">{{ subtitle }}</component>
    </div>
    <mixed-grid-filters
      extra-classes="filters--movements"
      :title="$t('MovementGrid.filters.panel.title')"
      :filters="filters"
      :products-count="filteredColumnsCount"
      :unique-id="uniqueId"
      :default-sort="defaultSort"
      :default-filters="activeFilters"
      :has-filters-in-url="hasFiltersInUrl"
      :should-display-available-online="false"
      should-display-products-count-only-when-filtered
      novelty-filter-id=""
      :novelty-product-property-value-ids="[]"
      :show-sort-select="false"
      @on-sort-change="onSortChange"
      @on-filters-changed="onFiltersChanged"
    >
      <template #extra-actions>
        <div></div>
        <input
          :value="searchTerm"
          type="text"
          class="input input--fluid"
          :placeholder="$t('MovementGrid.filters.searchPlaceholder')"
          @input="debouncedSearch"
        />
      </template>
    </mixed-grid-filters>

    <div v-if="searchFilteredColumns.length > 0" :class="gridClasses">
      <movement-grid-item
        v-for="column in searchFilteredColumns"
        :key="column.data.itemId"
        v-bind="column.data"
        data-cy="movement-grid-item"
        :class="getColumnDisplayClasses(column)"
        @click="() => onClick(column.data)"
      />
    </div>
    <PhxsgSection v-else use="p">
      {{ $t('MovementGrid.noResults') }}
    </PhxsgSection>

    <ul v-if="shouldDisplayLoadMore" :class="loadMoreClasses" role="presentation">
      <li role="presentation">
        <phxsg-button variant="secondary" class="actions--center mt-md" data-cy="mixed-grid-see-all" @click="seeAll">
          {{ $t('MovementGrid.filters.displayAll', { nb: nbOfMaxItems }) }}
        </phxsg-button>
      </li>
    </ul>
  </PhxsgContainer>
  <teleport to="#teleport-target-panels">
    <movement-grid-panel :id="panelId" :movement="selectedMovement" :watches="watches" />
  </teleport>
</template>
